<template>
  <documents-metadata
    ref="documentMetadataRef"
    :fetch-metadata-and-field-infos="getDocumentMetadata"
    :update-metadata-function="updateDocumentMetadata"
    :can-edit-metadata="canEditMetadata"
  >
    <template #value-KKS="{ data }">
      <div class="kks-selector-contaner">
        <kks-selector
          :key="kksPickerRerenderKey"
          v-model="data.value"
          class="kks-selector"
          :busy="isLoading"
          :can-remove-last="false"
          :disabled="!canEditMetadata"
          :label="t('documentDetailView.kksTree')"
          :location="projectLocation!"
          @change="updateKks"
        />
        <PVButton
          v-if="
            document.metadata.checkBotEstablishedKKS?.length && canEditMetadata
          "
          class="show-kks-proposals-button"
          :size="'small'"
          @click="toggleIsShowingKksProposals"
        >
          {{ getKksProposalsButtonLabel() }}
        </PVButton>
      </div>
      <check-bot-established-kks-panel
        :file="document"
        :is-active="isShowingKksProposals"
        :is-validating-kks="isValidatingKks"
        :kks-validation-list="kksValidationList"
        :mode="ProjectType.SUPPLIER_PROJECT"
        @document-data-changed="handleCheckBotKksSelected"
      />
    </template>
  </documents-metadata>
</template>

<script lang="ts" setup>
  import { useKksValidator } from '@/composables/useKksValidator';
  import { useRoles } from '@/composables/useRoles';
  import { useToast } from '@/composables/useToast';
  import { endpointsList } from '@/helpers';
  import { MetadataUpdateService, ProjectDataService } from '@/services';
  import axios from 'axios';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    IFileMetadataWithWithFieldsMetadataDto,
    ISupplierDocumentDetails,
    KKS_METADATA_FIELD,
    ProjectType,
    Role,
    Serialized,
  } from 'types';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { KksSelectorChangeEvent } from '../TreeSelect/KksSelector.types';
  import { removeEmptyStrings } from 'utils';
  import DocumentsMetadata from './DocumentsMetadata.vue';

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const props = defineProps<{
    projectId: string;
    document: Serialized<ISupplierDocumentDetails>;
  }>();

  const { t } = useI18n();
  const { isUserRole } = useRoles();
  const { createErrorToast } = useToast();
  const { kksValidationList, isValidatingKks, validateKks } = useKksValidator();

  const kksPickerRerenderKey = ref<number>(0);
  const isLoadingMetadata = ref(false);
  const projectLocation = ref<string | null>(null);
  const isShowingKksProposals = ref<boolean>(false);
  const documentMetadataRef = ref<InstanceType<
    typeof DocumentsMetadata
  > | null>(null);

  const isLoading = computed(() => {
    return isLoadingMetadata.value || projectLocation.value === null;
  });

  const metadataUpdateService = new MetadataUpdateService(
    ProjectType.SUPPLIER_PROJECT,
  );
  const projectDataService = new ProjectDataService(
    ProjectType.SUPPLIER_PROJECT,
  );

  const canEditMetadata = computed(() => {
    return (
      props.document.canEditMetadata &&
      isUserRole([Role.Admin, Role.DocuManager])
    );
  });

  async function getDocumentMetadata(): Promise<IFileMetadataWithWithFieldsMetadataDto> {
    try {
      const response = await axios.post<IFileMetadataWithWithFieldsMetadataDto>(
        endpointsList.documentsUploads.getFileMetadataByIds(
          props.projectId,
          true,
        ),
        [props.document._id],
      );
      return response.data;
    } catch (error) {
      createErrorToast();
      throw error;
    }
  }

  async function updateDocumentMetadata(
    metadataField: string,
    fieldValue: string | string[],
  ): Promise<void> {
    await metadataUpdateService.updateMultiValueMetadataProperty({
      projectId: props.projectId,
      documentOid: props.document._id,
      metadataField,
      values: Array.isArray(fieldValue) ? fieldValue : [fieldValue],
    });
    emit('document-data-changed');
  }

  onMounted(getProjectLocation);
  async function getProjectLocation(): Promise<void> {
    projectLocation.value = await projectDataService.getProjectLocation(
      props.projectId,
    );
  }

  async function updateKks(e: KksSelectorChangeEvent): Promise<void> {
    try {
      isLoadingMetadata.value = true;
      const kks = e.target.value.map((kks) => kks.kks_trimmed);
      if (!canEditMetadata.value) return;

      if (!kks.length) {
        kksPickerRerenderKey.value++;
        createErrorToast(
          t('documentDetailView.errors.cannotRemoveLastValue.long'),
        );
        return;
      }
      updateDocumentMetadata(KKS_METADATA_FIELD, kks);
    } catch {
      createErrorToast();
    } finally {
      isLoadingMetadata.value = false;
    }
  }

  async function handleCheckBotKksSelected(): Promise<void> {
    await documentMetadataRef.value?.getMetadataAndFieldInfos();
    kksPickerRerenderKey.value++;
    await validateKksAndCheckBotKks();
    emit('document-data-changed');
  }

  function toggleIsShowingKksProposals() {
    isShowingKksProposals.value = !isShowingKksProposals.value;
  }

  function getKksProposalsButtonLabel(): string {
    return isShowingKksProposals.value
      ? t('documentDetailView.hideKksProposals').toString()
      : t('documentDetailView.showKksProposals').toString();
  }

  watch([props.document.metadata, projectLocation], validateKksAndCheckBotKks, {
    deep: true,
    immediate: true,
  });
  async function validateKksAndCheckBotKks(): Promise<void> {
    const kks = getKksAndCheckBotKks();
    if (!kks?.length) return;
    if (!projectLocation.value) return;

    await validateKks(kks, projectLocation.value);
  }

  function getKksAndCheckBotKks(): string[] {
    const checkBotEstablishedKks =
      props.document.metadata.checkBotEstablishedKKS;
    const acceptedKks = props.document.metadata.KKS.split(
      DEFAULT_METADATA_VALUES_SEPARATOR,
    );

    return removeEmptyStrings([
      ...(acceptedKks ?? []),
      ...(checkBotEstablishedKks ?? []),
    ]);
  }
</script>

<style lang="scss" scoped>
  .kks-selector-contaner {
    display: flex;
    align-items: center;
    column-gap: 1rem;
    .show-kks-proposals-button {
      background-color: $primary-color;
      text-wrap: nowrap;
    }
  }
</style>
